import z from "zod";
import { normalizeProfileUrl } from "@/lib/profile-url";
import type { SearchReasonDto } from "./project.dto.js";

export const USER_INTERESTS = ["RESEARCHING", "FUNDING_RESEARCH"] as const;
export type UserInterest = (typeof USER_INTERESTS)[number];

export const USER_INTERESTS_OPTIONS: { label: string; value: UserInterest }[] =
  [
    {
      label: "Applying for funds as a researcher",
      value: "RESEARCHING",
    },
    {
      label: "Funding, advising, or browsing projects",
      value: "FUNDING_RESEARCH",
    },
  ];

/** Scheme-optional: a bare domain is accepted and stored as canonical HTTPS. */
export const ProfileUrlSchema = z.string().transform((value, ctx) => {
  try {
    return normalizeProfileUrl(value);
  } catch {
    ctx.addIssue({
      code: "custom",
      message: "Enter a valid website address (https:// is optional)",
    });
    return z.NEVER;
  }
});

export const ORCID_REGEX = /^\d{4}-\d{4}-\d{4}-\d{3}[\dX]$/;

/** Accepts a bare ORCID iD or its https://orcid.org/… URL form; submits the bare iD. */
export const OrcidSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/^https?:\/\/(www\.)?orcid\.org\//i, ""))
  .pipe(z.string().regex(ORCID_REGEX, "Invalid ORCID iD"));

/** User-editable externalIds (openAlex/scopus are sync-managed). `null` deletes a key. */
export const UpdateExternalIdsSchema = z
  .object({
    orcid: OrcidSchema.nullable(),
    googleScholar: z.string().trim().nullable(),
    linkedin: z.string().trim().nullable(),
    personalWebsite: ProfileUrlSchema.nullable(),
    twitter: z.string().trim().min(1).max(100).nullable(),
    wikipedia: z.url().nullable(),
  })
  .partial();

export const UpdateUserSchema = z
  .object({
    name: z.string().min(1),
    handle: z.string().nullable(),
    about: z.string(),
    profileUrl: ProfileUrlSchema,
    institutionId: z.string().nullable(),
    interests: z.array(z.enum(USER_INTERESTS)).min(1),
    emailPreferences: z.object({ postUpdates: z.boolean() }),
    followingListPublic: z.boolean(),
    donationsEnabled: z.boolean(),
    topics: z.array(z.string().trim().min(1).max(80)),
    externalIds: UpdateExternalIdsSchema,
  })
  .partial();
export type UpdateUserDto = z.infer<typeof UpdateUserSchema>;
export type UpdateUserInput = z.input<typeof UpdateUserSchema>;

export interface ExternalIds {
  openAlex?: string;
  orcid?: string;
  scopus?: string;
  twitter?: string;
  wikipedia?: string;
  googleScholar?: string;
  linkedin?: string;
  personalWebsite?: string;
}

export interface UserCardDto {
  id: string;
  name: string;
  avatar: string | null;
  handle: string | null;
  interests: UserInterest[];
  about: string | null;
  institution: { id: string; name: string } | null;
  externalIds: ExternalIds | null;
  /** False for profiles indexed from public data that haven't joined. */
  isMember?: boolean;
  reasons?: SearchReasonDto[];
}
